/* eslint-disable ember/no-computed-properties-in-native-classes */
import { computed, set } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import type { SegmentationItem } from 'lumen/components/hotel-and-market/revenue-insight/base/segmentation-block-item-base';
import { KPIS } from 'lumen/utils/constants/ri-constant-values';

export type SegmentationItemLike = Partial<SegmentationItem>;

export interface HotelAndMarketRevenueInsightBaseSegmentationBlockBaseArgs {
  filterLimits: {
    [key: string]: number;
  };
  isLoading: boolean;
  kpi: string;
  parsedSegmentationFilters: unknown;
  searchValues: {
    isSearchActive: boolean;
    searchValue: string;
  };
  segmentation: {
    segmentationName: string;
    sortedSegmentationObjects: SegmentationItem[];
  };
  segmentationBlockIdx: number;
  segmentationFilterClicked: unknown;
  setIsSearchActive: (
    segmentationName: string,
    isSearchActive: boolean,
  ) => void;
  setSearchValue: (segmentationName: string, searchValue: string) => void;
  visibleKpis: unknown;
}

export interface HotelAndMarketRevenueInsightBaseSegmentationBlockBaseSignature {
  Args: HotelAndMarketRevenueInsightBaseSegmentationBlockBaseArgs;
}

interface HotelAndMarketRevenueInsightBaseSegmentationBlockBase<
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  T extends HotelAndMarketRevenueInsightBaseSegmentationBlockBaseSignature,
> {
  updatedExpandedParents?: (parentSegmentationValueId: string) => void;
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
abstract class HotelAndMarketRevenueInsightBaseSegmentationBlockBase<
  T extends HotelAndMarketRevenueInsightBaseSegmentationBlockBaseSignature,
> extends Component<T> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare riSelections: Services['ri-selections'];

  get contentParams(): {
    filterLimits: { [key: string]: number };
    isLoading: boolean;
    segmentationBlockIdx: number;
    segmentationFilterClicked: unknown;
    segmentationName: string;
    updateIsSearchActive: (isSearchActive: boolean) => void;
    updatedExpandedParents?: (parentSegmentationValueId: string) => void;
    visibleKpis: unknown;
  } {
    return {
      segmentationName: this.segmentationName,
      isLoading: this.args.isLoading,
      segmentationBlockIdx: this.args.segmentationBlockIdx,
      filterLimits: this.args.filterLimits,
      updateIsSearchActive: this.updateIsSearchActive.bind(this),
      updatedExpandedParents: this.updatedExpandedParents,
      segmentationFilterClicked: this.args.segmentationFilterClicked,
      visibleKpis: this.args.visibleKpis,
    };
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentSelectedFilters() {
    // The segmentation filters active for this breakdown
    return (
      this.riSelections.parsedSegmentationFilters[this.segmentationName] || []
    );
  }

  @computed('args.searchValues.isSearchActive')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSearchActive() {
    return this.args.searchValues?.isSearchActive;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get maxBlockValues() {
    let maxValue = 0;
    let maxChangeValue = 0;
    let maxWithPLY = 0;
    const { bobKey, changeKey } = this.riSelections;

    this.visibleSegmentations.forEach((segmentationObj) => {
      if (segmentationObj.hasNoData) {
        // This means it was a remaining value, these have no data so these should not be used for max calculation
        return;
      }
      const PLY =
        segmentationObj.calculatedValues.pickupLastYear[this.args.kpi] ?? null;
      const value = Math.abs(
        segmentationObj.calculatedValues[bobKey][this.args.kpi] ?? 0,
      );
      const valueChange = Math.abs(
        segmentationObj.calculatedValues[changeKey][this.args.kpi] ?? 0,
      );

      maxValue = Math.max(value, maxValue);
      maxChangeValue = Math.max(valueChange, maxChangeValue);

      if (
        this.riSelections?.isBudgetActive &&
        segmentationObj.calculatedValues.budget
      ) {
        const valueBudget = Math.abs(
          segmentationObj.calculatedValues.budget[this.args.kpi] ?? 0,
        );
        maxValue = Math.max(valueBudget, maxValue);
        // Pickup last year can be the max value for Budget
        // Don't show Pickup Last Year for ADR
        if (this.args.kpi !== KPIS.ADR) {
          maxWithPLY = Math.max(value + (PLY ?? 0), maxWithPLY);
          maxValue = Math.max(maxWithPLY, maxValue);
        }
      }

      if (
        this.riSelections?.isForecastActive &&
        segmentationObj.calculatedValues.forecast
      ) {
        const valueForecast = Math.abs(
          segmentationObj.calculatedValues.forecast[this.args.kpi] ?? 0,
        );
        maxValue = Math.max(valueForecast, maxValue);
        // Pickup last year can be the max value for Forecast
        // Don't show Pickup Last Year for ADR
        if (this.args.kpi !== KPIS.ADR) {
          maxWithPLY = Math.max(value + (PLY ?? 0), maxWithPLY);
          maxValue = Math.max(maxWithPLY, maxValue);
        }
      }
    });

    return {
      BOB: maxValue,
      change: maxChangeValue,
    };
  }

  @computed('args.searchValues.searchValue')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get searchValue() {
    return this.args.searchValues?.searchValue;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get segmentationCategoryMappings() {
    return this.hotelAndMarketService.user?.segmentationCategoryMappings;
  }

  @computed('args.segmentation.segmentationName')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get segmentationName() {
    return this.args.segmentation?.segmentationName;
  }

  // Overriden in subclass!
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showExpandAndSearch() {
    return true;
  }

  @computed('args.segmentation.sortedSegmentationObjects')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sortedSegmentationObjects() {
    return this.args.segmentation?.sortedSegmentationObjects;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get titleParams() {
    return {
      segmentationName: this.segmentationName,
      kpi: this.args.kpi,
      searchValues: this.args.searchValues,
      updateSearchValue: this.updateSearchValue.bind(this),
      updateIsSearchActive: this.updateIsSearchActive.bind(this),
      isLoading: this.args.isLoading,
      showExpandAndSearch: this.showExpandAndSearch,
    };
  }

  @computed(
    'args.isLoading',
    'sortedSegmentationObjects.@each.segmentationValueId',
    'expandedIds',
    'searchValue',
    'isSearchActive',
    'currentSelectedFilters.[]',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get visibleSegmentations() {
    if (this.args.isLoading) {
      return [];
    }

    if (!this.isSearchActive) {
      return this.sortedSegmentationObjects;
    }

    const visibleSegmentations: SegmentationItem[] = [];
    this.sortedSegmentationObjects.forEach((item) => {
      set(item, 'isExpanded', false);
      this.findMatchingItems(item, visibleSegmentations);
    });
    return visibleSegmentations;
  }

  /**
   * This function finds values in the remaining values array
   * because all we have is a segmentation value id we make a dummy object to show
   * we can identify these values by the hasNoData property
   * @param {array} segmentation : This is the aggregated row
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  findAndMapRemainingValues(segmentation: SegmentationItem) {
    if (!this.searchValue) {
      return [];
    }

    return segmentation.remainingValues
      .filter(
        (value) =>
          value.toLowerCase().indexOf(this.searchValue.toLowerCase()) !== -1,
      )
      .map((value) => ({
        segmentationId: segmentation.segmentationId,
        parent: segmentation.parent,
        segmentationValueId: value,
        segmentationValue: value,
        displayValue: value,
        hasNoData: true,
        isSelectable: true,

        isSelected: this.currentSelectedFilters.indexOf(value) !== -1,
      }));
  }

  findMatchingItems(
    item: SegmentationItem,
    collection: SegmentationItemLike[],
  ): void {
    let matchedItems: SegmentationItemLike[] = [];
    const matchedChildren: SegmentationItemLike[] = [];
    let matchedRemainingValues: SegmentationItemLike[] = [];
    const isFound =
      item.displayValue
        .toLowerCase()
        .indexOf(this.searchValue.toLowerCase()) !== -1;

    if (item.children) {
      // recursion 🧙
      item.children.forEach((child) =>
        this.findMatchingItems(child, matchedChildren),
      );
    }

    if (item.remainingValues) {
      matchedRemainingValues = [
        ...matchedRemainingValues,
        ...this.findAndMapRemainingValues(item),
      ];
    }

    if (isFound || matchedChildren.length) {
      matchedItems.push(item);
    }

    if (matchedChildren.length) {
      // If children are found show as expanded
      set(item, 'isExpanded', true);
      matchedItems = [...matchedItems, ...matchedChildren];
    }

    if (matchedRemainingValues.length) {
      matchedItems = [...matchedItems, ...matchedRemainingValues];
    }

    collection.push(...matchedItems);
  }

  updateIsSearchActive(isSearchActive: boolean): void {
    this.args.setIsSearchActive(this.segmentationName, isSearchActive);
  }

  updateSearchValue(searchValue: string): void {
    this.args.setSearchValue(this.segmentationName, searchValue);
  }
}

export default HotelAndMarketRevenueInsightBaseSegmentationBlockBase;
